/**
 * Profile Utilities for PeopleTag
 * Comprehensive utility functions for profile management, analytics, sharing, and data processing
 */

/**
 * Calculate profile completeness score (0-100)
 * @param {Object} profile - User profile object
 * @returns {number} Completeness score (0-100)
 */
export const calculateProfileCompleteness = (profile) => {
  if (!profile) return 0;

  const fields = {
    username: profile.username ? 10 : 0,
    name: profile.name ? 10 : 0,
    bio: profile.bio ? 15 : 0,
    tags: profile.tagsList && profile.tagsList.length > 0 ? Math.min(profile.tagsList.length * 5, 20) : 0,
    socials: profile.socialLinks && profile.socialLinks.length > 0 ? Math.min(profile.socialLinks.length * 5, 15) : 0,
    city: profile.city ? 10 : 0,
    location: profile.location && profile.location.lat && profile.location.lng ? 10 : 0,
    birthDate: profile.birthDate ? 10 : 0,
    avatar: profile.avatar ? 10 : 0,
  };

  return Math.min(100, Object.values(fields).reduce((sum, val) => sum + val, 0));
};

/**
 * Validate profile data
 * @param {Object} profile - Profile data to validate
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateProfile = (profile) => {
  const errors = [];

  if (!profile) {
    return { isValid: false, errors: ['Profile data is required'] };
  }

  // Username validation
  if (!profile.username || profile.username.trim() === '') {
    errors.push('Username is required');
  } else if (profile.username.length < 3) {
    errors.push('Username must be at least 3 characters');
  } else if (profile.username.length > 22) {
    errors.push('Username must be no more than 22 characters');
  } else if (!/^[a-zA-Z0-9_-]+$/.test(profile.username)) {
    errors.push('Username can only contain letters, numbers, underscores, and hyphens');
  }

  // Email validation (if provided)
  if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
    errors.push('Invalid email format');
  }

  // Bio validation
  if (profile.bio && profile.bio.length > 1000) {
    errors.push('Bio must be less than 1000 characters');
  }

  // Tags validation
  if (profile.tagsList) {
    if (!Array.isArray(profile.tagsList)) {
      errors.push('Tags must be an array');
    } else if (profile.tagsList.length > 50) {
      errors.push('Maximum 50 tags allowed');
    } else {
      profile.tagsList.forEach((tag, index) => {
        if (typeof tag !== 'string') {
          errors.push(`Tag at index ${index} must be a string`);
        } else if (tag.length > 50) {
          errors.push(`Tag at index ${index} exceeds 50 characters`);
        }
      });
    }
  }

  // Social links validation
  if (profile.socialLinks) {
    if (!Array.isArray(profile.socialLinks)) {
      errors.push('Social links must be an array');
    } else {
      profile.socialLinks.forEach((link, index) => {
        if (typeof link !== 'string') {
          errors.push(`Social link at index ${index} must be a string`);
        } else if (link && !isValidUrl(link)) {
          errors.push(`Social link at index ${index} is not a valid URL`);
        }
      });
    }
  }

  // Birth date validation
  if (profile.birthDate) {
    const birthDate = new Date(profile.birthDate);
    if (isNaN(birthDate.getTime())) {
      errors.push('Invalid birth date format');
    } else {
      const age = calculateAge(profile.birthDate);
      if (age < 13) {
        errors.push('Must be at least 13 years old');
      } else if (age > 150) {
        errors.push('Invalid birth date');
      }
    }
  }

  // Location validation
  if (profile.location) {
    if (typeof profile.location !== 'object') {
      errors.push('Location must be an object');
    } else {
      if (profile.location.lat !== undefined && (isNaN(profile.location.lat) || profile.location.lat < -90 || profile.location.lat > 90)) {
        errors.push('Invalid latitude (must be between -90 and 90)');
      }
      if (profile.location.lng !== undefined && (isNaN(profile.location.lng) || profile.location.lng < -180 || profile.location.lng > 180)) {
        errors.push('Invalid longitude (must be between -180 and 180)');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if a URL is valid
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Calculate age from birth date
 * @param {string|Date} birthDate - Birth date
 * @returns {number} Age in years
 */
export const calculateAge = (birthDate) => {
  if (!birthDate) return null;
  
  let birth;
  
  // Handle DD.MM.YYYY format (e.g., "15.03.1990")
  if (typeof birthDate === 'string' && birthDate.includes('.')) {
    const parts = birthDate.split('.');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
      const year = parseInt(parts[2], 10);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        birth = new Date(year, month, day);
      } else {
        return null;
      }
    } else {
      // Try standard Date parsing as fallback
      birth = new Date(birthDate);
    }
  } else {
    // Try standard Date parsing
    birth = new Date(birthDate);
  }
  
  // Validate the date
  if (isNaN(birth.getTime())) {
    return null;
  }
  
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  // Validate age is reasonable
  if (age < 0 || age > 150) {
    return null;
  }
  
  return age;
};

/**
 * Export profile data as JSON
 * @param {Object} profile - Profile to export
 * @param {boolean} includePrivate - Whether to include private/unpublished data
 * @returns {string} JSON string
 */
export const exportProfile = (profile, includePrivate = true) => {
  if (!profile) {
    throw new Error('Profile is required');
  }

  const exportData = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    profile: {
      username: profile.username,
      name: profile.name || null,
      bio: profile.bio || null,
      city: profile.city || null,
      location: profile.location || null,
      birthDate: profile.birthDate || null,
      tagsList: profile.tagsList || [],
      socialLinks: profile.socialLinks || [],
      avatar: profile.avatar || null,
    }
  };

  if (includePrivate) {
    exportData.profile.email = profile.email || null;
    exportData.profile.walletAddress = profile.walletAddress || null;
  }

  return JSON.stringify(exportData, null, 2);
};

/**
 * Import profile data from JSON
 * @param {string} jsonString - JSON string to import
 * @returns {Object} Parsed profile data
 */
export const importProfile = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    
    if (!data.profile) {
      throw new Error('Invalid profile format: missing profile data');
    }

    return {
      username: data.profile.username || '',
      name: data.profile.name || '',
      bio: data.profile.bio || '',
      city: data.profile.city || '',
      location: data.profile.location || null,
      birthDate: data.profile.birthDate || '',
      tagsList: Array.isArray(data.profile.tagsList) ? data.profile.tagsList : [],
      socialLinks: Array.isArray(data.profile.socialLinks) ? data.profile.socialLinks : [],
      avatar: data.profile.avatar || null,
      email: data.profile.email || '',
    };
  } catch (error) {
    throw new Error(`Failed to import profile: ${error.message}`);
  }
};

/**
 * Download profile as JSON file
 * @param {Object} profile - Profile to download
 * @param {string} filename - Optional filename
 */
export const downloadProfileAsFile = (profile, filename = null) => {
  const jsonString = exportProfile(profile, true);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `peopletag-profile-${profile.username || 'export'}-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate shareable profile link
 * @param {string} username - Username
 * @param {string} baseUrl - Base URL of the application
 * @returns {string} Shareable URL
 */
export const generateProfileLink = (username, baseUrl = window.location.origin) => {
  return `${baseUrl}/profile/${encodeURIComponent(username)}`;
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

/**
 * Calculate match score between two profiles based on tags
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {number} Match score (0-100)
 */
export const calculateMatchScore = (profile1, profile2) => {
  if (!profile1 || !profile2) return 0;

  const tags1 = new Set((profile1.tagsList || []).map(t => t.toLowerCase().trim()));
  const tags2 = new Set((profile2.tagsList || []).map(t => t.toLowerCase().trim()));

  if (tags1.size === 0 && tags2.size === 0) return 50; // Neutral score if both have no tags
  if (tags1.size === 0 || tags2.size === 0) return 0;

  const intersection = new Set([...tags1].filter(t => tags2.has(t)));
  const union = new Set([...tags1, ...tags2]);

  // Jaccard similarity coefficient
  const similarity = intersection.size / union.size;
  return Math.round(similarity * 100);
};

/**
 * Calculate distance between two locations (Haversine formula)
 * @param {Object} loc1 - Location 1 with lat and lng
 * @param {Object} loc2 - Location 2 with lat and lng
 * @param {string} unit - 'km' or 'mi'
 * @returns {number} Distance in specified unit
 */
export const calculateDistance = (loc1, loc2, unit = 'km') => {
  if (!loc1 || !loc2 || !loc1.lat || !loc1.lng || !loc2.lat || !loc2.lng) {
    return null;
  }

  const R = unit === 'km' ? 6371 : 3959; // Earth radius in km or miles
  const dLat = toRad(loc2.lat - loc1.lat);
  const dLon = toRad(loc2.lng - loc1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(loc1.lat)) * Math.cos(toRad(loc2.lat)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return Math.round(distance * 10) / 10; // Round to 1 decimal place
};

const toRad = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Get profile statistics
 * @param {Object} profile - Profile to analyze
 * @returns {Object} Statistics object
 */
export const getProfileStatistics = (profile) => {
  if (!profile) return null;

  return {
    completeness: calculateProfileCompleteness(profile),
    tagCount: profile.tagsList ? profile.tagsList.length : 0,
    socialCount: profile.socialLinks ? profile.socialLinks.filter(link => link && link.trim() !== '').length : 0,
    age: profile.birthDate ? calculateAge(profile.birthDate) : null,
    hasLocation: !!(profile.location && profile.location.lat && profile.location.lng),
    hasBio: !!(profile.bio && profile.bio.trim() !== ''),
    hasAvatar: !!profile.avatar,
    bioLength: profile.bio ? profile.bio.length : 0,
    bioWordCount: profile.bio ? profile.bio.trim().split(/\s+/).filter(word => word.length > 0).length : 0,
  };
};

/**
 * Find duplicate or similar profiles
 * @param {Object} profile - Profile to check
 * @param {Array} allProfiles - Array of all profiles
 * @param {number} similarityThreshold - Minimum similarity score (0-100)
 * @returns {Array} Array of similar profiles with similarity scores
 */
export const findSimilarProfiles = (profile, allProfiles, similarityThreshold = 70) => {
  if (!profile || !allProfiles || allProfiles.length === 0) return [];

  const similar = allProfiles
    .filter(p => p.username !== profile.username) // Exclude self
    .map(p => ({
      profile: p,
      similarity: calculateMatchScore(profile, p)
    }))
    .filter(item => item.similarity >= similarityThreshold)
    .sort((a, b) => b.similarity - a.similarity);

  return similar;
};

/**
 * Filter profiles by multiple criteria
 * @param {Array} profiles - Array of profiles to filter
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered profiles
 */
export const filterProfiles = (profiles, filters) => {
  if (!profiles || profiles.length === 0) return [];
  if (!filters || Object.keys(filters).length === 0) return profiles;

  return profiles.filter(profile => {
    // Tag filter
    if (filters.tags && filters.tags.length > 0) {
      const profileTags = (profile.tagsList || []).map(t => t.toLowerCase().trim());
      const filterTags = filters.tags.map(t => t.toLowerCase().trim());
      const hasAllTags = filterTags.every(tag => profileTags.includes(tag));
      if (!hasAllTags) return false;
    }

    // Age range filter
    if (filters.minAge || filters.maxAge) {
      const age = calculateAge(profile.birthDate);
      if (age === null) return false;
      if (filters.minAge && age < filters.minAge) return false;
      if (filters.maxAge && age > filters.maxAge) return false;
    }

    // City filter
    if (filters.cities && filters.cities.length > 0) {
      const profileCity = (profile.city || '').toLowerCase().trim();
      const hasCity = filters.cities.some(city => 
        profileCity === city.toLowerCase().trim()
      );
      if (!hasCity) return false;
    }

    // Distance filter (requires reference location)
    if (filters.maxDistance && filters.referenceLocation && profile.location) {
      const distance = calculateDistance(filters.referenceLocation, profile.location, filters.distanceUnit || 'km');
      if (distance === null || distance > filters.maxDistance) return false;
    }

    // Has bio filter
    if (filters.hasBio !== undefined) {
      const hasBio = !!(profile.bio && profile.bio.trim() !== '');
      if (hasBio !== filters.hasBio) return false;
    }

    // Has avatar filter
    if (filters.hasAvatar !== undefined) {
      const hasAvatar = !!profile.avatar;
      if (hasAvatar !== filters.hasAvatar) return false;
    }

    // Search query filter
    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const query = filters.searchQuery.toLowerCase().trim();
      const searchableText = [
        profile.username,
        profile.name,
        profile.bio,
        profile.city,
        ...(profile.tagsList || []),
        ...(profile.socialLinks || [])
      ].filter(Boolean).join(' ').toLowerCase();

      if (!searchableText.includes(query)) return false;
    }

    return true;
  });
};

/**
 * Sort profiles by various criteria
 * @param {Array} profiles - Array of profiles to sort
 * @param {string} sortBy - Sort criteria: 'match', 'distance', 'name', 'city', 'age', 'completeness', 'recent'
 * @param {Object} options - Additional options (referenceProfile, referenceLocation, etc.)
 * @returns {Array} Sorted profiles
 */
export const sortProfiles = (profiles, sortBy = 'match', options = {}) => {
  if (!profiles || profiles.length === 0) return [];

  const sorted = [...profiles];

  switch (sortBy) {
    case 'match':
      if (options.referenceProfile) {
        sorted.sort((a, b) => {
          const scoreA = calculateMatchScore(options.referenceProfile, a);
          const scoreB = calculateMatchScore(options.referenceProfile, b);
          return scoreB - scoreA;
        });
      }
      break;

    case 'distance':
      if (options.referenceLocation) {
        sorted.sort((a, b) => {
          const distA = a.location ? calculateDistance(options.referenceLocation, a.location) : Infinity;
          const distB = b.location ? calculateDistance(options.referenceLocation, b.location) : Infinity;
          return (distA || Infinity) - (distB || Infinity);
        });
      }
      break;

    case 'name':
      sorted.sort((a, b) => {
        const nameA = (a.name || a.username || '').toLowerCase();
        const nameB = (b.name || b.username || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      break;

    case 'city':
      sorted.sort((a, b) => {
        const cityA = (a.city || '').toLowerCase();
        const cityB = (b.city || '').toLowerCase();
        return cityA.localeCompare(cityB);
      });
      break;

    case 'age':
      sorted.sort((a, b) => {
        const ageA = calculateAge(a.birthDate) || 0;
        const ageB = calculateAge(b.birthDate) || 0;
        return ageA - ageB;
      });
      break;

    case 'completeness':
      sorted.sort((a, b) => {
        const compA = calculateProfileCompleteness(a);
        const compB = calculateProfileCompleteness(b);
        return compB - compA;
      });
      break;

    case 'recent':
      sorted.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });
      break;

    default:
      break;
  }

  return sorted;
};

/**
 * Get popular tags from profiles
 * @param {Array} profiles - Array of profiles
 * @param {number} limit - Maximum number of tags to return
 * @returns {Array} Array of {tag: string, count: number} sorted by count
 */
export const getPopularTags = (profiles, limit = 20) => {
  if (!profiles || profiles.length === 0) return [];

  const tagCounts = {};

  profiles.forEach(profile => {
    if (profile.tagsList && Array.isArray(profile.tagsList)) {
      profile.tagsList.forEach(tag => {
        const normalizedTag = tag.toLowerCase().trim();
        if (normalizedTag) {
          tagCounts[normalizedTag] = (tagCounts[normalizedTag] || 0) + 1;
        }
      });
    }
  });

  return Object.entries(tagCounts)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

/**
 * Get tag suggestions based on existing tags
 * @param {Array} currentTags - Current tags
 * @param {Array} allProfiles - All profiles for analysis
 * @param {number} limit - Maximum suggestions
 * @returns {Array} Suggested tags
 */
export const getTagSuggestions = (currentTags, allProfiles, limit = 10) => {
  if (!currentTags || currentTags.length === 0 || !allProfiles) return [];

  const currentTagsSet = new Set(currentTags.map(t => t.toLowerCase().trim()));
  const coOccurrence = {};

  allProfiles.forEach(profile => {
    if (profile.tagsList && Array.isArray(profile.tagsList)) {
      const profileTags = profile.tagsList.map(t => t.toLowerCase().trim());
      const hasCurrentTag = profileTags.some(tag => currentTagsSet.has(tag));

      if (hasCurrentTag) {
        profileTags.forEach(tag => {
          if (!currentTagsSet.has(tag)) {
            coOccurrence[tag] = (coOccurrence[tag] || 0) + 1;
          }
        });
      }
    }
  });

  return Object.entries(coOccurrence)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(item => item.tag);
};

/**
 * Format profile for display
 * @param {Object} profile - Profile to format
 * @param {Object} options - Formatting options
 * @returns {Object} Formatted profile
 */
export const formatProfileForDisplay = (profile, options = {}) => {
  if (!profile) return null;

  const formatted = {
    ...profile,
    displayName: profile.name || profile.username || 'Anonymous',
    displayCity: profile.city || 'Location not set',
    displayAge: profile.birthDate ? `${calculateAge(profile.birthDate)} years old` : 'Age not set',
    tagCount: profile.tagsList ? profile.tagsList.length : 0,
    socialCount: profile.socialLinks ? profile.socialLinks.filter(link => link && link.trim() !== '').length : 0,
    completeness: calculateProfileCompleteness(profile),
  };

  if (options.includeStatistics) {
    formatted.statistics = getProfileStatistics(profile);
  }

  return formatted;
};

/**
 * Sanitize profile data (remove sensitive information)
 * @param {Object} profile - Profile to sanitize
 * @param {Array} fieldsToRemove - Fields to remove
 * @returns {Object} Sanitized profile
 */
export const sanitizeProfile = (profile, fieldsToRemove = ['email', 'walletAddress', 'ipfsHash']) => {
  if (!profile) return null;

  const sanitized = { ...profile };
  fieldsToRemove.forEach(field => {
    delete sanitized[field];
  });

  return sanitized;
};

/**
 * Merge two profiles (useful for updates)
 * @param {Object} baseProfile - Base profile
 * @param {Object} updates - Profile updates
 * @returns {Object} Merged profile
 */
export const mergeProfiles = (baseProfile, updates) => {
  if (!baseProfile) return updates || {};
  if (!updates) return baseProfile;

  return {
    ...baseProfile,
    ...updates,
    tagsList: updates.tagsList !== undefined ? updates.tagsList : baseProfile.tagsList,
    socialLinks: updates.socialLinks !== undefined ? updates.socialLinks : baseProfile.socialLinks,
    location: updates.location !== undefined ? updates.location : baseProfile.location,
  };
};

/**
 * Check if profile has required fields for publishing
 * @param {Object} profile - Profile to check
 * @returns {Object} { canPublish: boolean, missingFields: string[] }
 */
export const canPublishProfile = (profile) => {
  if (!profile) {
    return { canPublish: false, missingFields: ['profile'] };
  }

  const requiredFields = ['username'];
  const missingFields = requiredFields.filter(field => !profile[field]);

  return {
    canPublish: missingFields.length === 0,
    missingFields
  };
};

/**
 * Generate profile summary text
 * @param {Object} profile - Profile to summarize
 * @param {number} maxLength - Maximum length of summary
 * @returns {string} Summary text
 */
export const generateProfileSummary = (profile, maxLength = 200) => {
  if (!profile) return '';

  const parts = [];

  if (profile.name) {
    parts.push(profile.name);
  }

  if (profile.bio) {
    parts.push(profile.bio);
  }

  if (profile.city) {
    parts.push(`Located in ${profile.city}`);
  }

  if (profile.tagsList && profile.tagsList.length > 0) {
    parts.push(`Interests: ${profile.tagsList.slice(0, 5).join(', ')}`);
  }

  const summary = parts.join('. ');
  return summary.length > maxLength ? summary.substring(0, maxLength - 3) + '...' : summary;
};

/**
 * Compare two profiles
 * @param {Object} profile1 - First profile
 * @param {Object} profile2 - Second profile
 * @returns {Object} Comparison result
 */
export const compareProfiles = (profile1, profile2) => {
  if (!profile1 || !profile2) return null;

  const tags1 = new Set((profile1.tagsList || []).map(t => t.toLowerCase().trim()));
  const tags2 = new Set((profile2.tagsList || []).map(t => t.toLowerCase().trim()));
  const commonTags = [...tags1].filter(t => tags2.has(t));
  const uniqueTags1 = [...tags1].filter(t => !tags2.has(t));
  const uniqueTags2 = [...tags2].filter(t => !tags1.has(t));

  const distance = (profile1.location && profile2.location) 
    ? calculateDistance(profile1.location, profile2.location)
    : null;

  return {
    matchScore: calculateMatchScore(profile1, profile2),
    commonTags,
    uniqueTags1,
    uniqueTags2,
    distance,
    sameCity: profile1.city && profile2.city && 
              profile1.city.toLowerCase().trim() === profile2.city.toLowerCase().trim(),
    ageDifference: (profile1.birthDate && profile2.birthDate) 
      ? Math.abs(calculateAge(profile1.birthDate) - calculateAge(profile2.birthDate))
      : null,
  };
};

